import { useCallback, useEffect, useState } from 'react';
import { api, formatTarih, formatTl } from '../lib/api';
import AraSec from '../ui/AraSec';
import KalemdenUrun, { type YeniUrunGirdi } from './KalemdenUrun';
import { useAuth } from '../lib/auth';

type Urun = { id: number; code: string; name: string; unit: string };
type Satir = {
  id: number; supplierCode: string | null; description: string;
  qty: number; unit: string | null; unitPriceKurus: number;
  priceDeltaPct: number | null;
  bagli: Urun | null; oneri: Urun | null; oneriKesin?: boolean;
};
type Detay = {
  id: number; no: string; date: string; totalKurus: number; status: string;
  supplier: { id: number; name: string } | null;
  satirlar: Satir[]; itirazKalanGun: number;
};

/**
 * FATURAYA BAĞLA — satır satır ürün eşleme.
 *
 * Öneri gösterilir ama otomatik bağlanmaz: yanlış eşleşme stoğu bozar ve geri
 * almak elle hareket gerektirir. Kullanıcı onaylar, sistem üç işi birden yapar
 * (takma ad + stok girişi + fiyat geçmişi).
 */
export default function AlisDetay({
  id, kapat, degisti,
}: { id: number; kapat: () => void; degisti: () => void }) {
  const { can } = useAuth();
  const [d, setD] = useState<Detay | null>(null);
  const [urunler, setUrunler] = useState<Urun[]>([]);
  const [secim, setSecim] = useState<Record<number, string>>({});
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [calisan, setCalisan] = useState<number | null>(null);

  const yukle = useCallback(async () => {
    const r = await api<Detay>(`/api/alis/${id}`);
    setD(r);
    // Yalnız koddan gelen kesin öneri kutuda hazır gelir; ada göre tahmin seçili gelmez.
    setSecim(Object.fromEntries(
      r.satirlar.filter((s) => !s.bagli && s.oneri && s.oneriKesin).map((s) => [s.id, String(s.oneri!.id)])
    ));
  }, [id]);

  useEffect(() => {
    yukle().catch((e) => setHata((e as Error).message));
    api<{ satirlar: Urun[] }>('/api/urunler?durum=hepsi&limit=2000&sirala=kod')
      .then((r) => setUrunler(r.satirlar)).catch(() => undefined);
  }, [yukle]);

  /** Sistemde yoksa: faturadaki kalemden ürün kartı aç, satırı ona bağla. */
  async function urunAc(satir: Satir, g: YeniUrunGirdi, stogaGirme: boolean) {
    setHata(null); setCalisan(satir.id);
    try {
      const r = await api<{ code: string; giren: number; birim: string; uyari: string | null }>(
        `/api/alis/satir/${satir.id}/urun-ac`, { method: 'POST', body: JSON.stringify({ ...g, stogaGirme }) });
      setBilgi(`${r.code} ürün kartı açıldı ve satır bağlandı. ` + (r.giren > 0 ? `Stoğa ${r.giren} ${r.birim.toLowerCase()} girdi. ` : '') + (r.uyari ?? ''));
      await yukle(); degisti();
      api<{ satirlar: Urun[] }>('/api/urunler?durum=hepsi&limit=2000').then((x) => setUrunler(x.satirlar)).catch(() => undefined);
    } catch (e) { setHata(e instanceof Error ? e.message : 'Ürün açılamadı'); }
    finally { setCalisan(null); }
  }

  async function bagla(satir: Satir, stogaGirme = false) {
    const productId = Number(secim[satir.id]);
    if (!productId) { setHata('Önce ürün seçin'); return; }
    setHata(null); setCalisan(satir.id);
    try {
      const r = await api<{ giren: number; birim: string; artisYuzde: number | null; uyari: string | null }>(
        `/api/alis/satir/${satir.id}/bagla`,
        { method: 'POST', body: JSON.stringify({ productId, aliasKaydet: true, stogaGirme }) }
      );
      setBilgi(
        (r.giren > 0 ? `Stoğa ${r.giren} ${r.birim.toLowerCase()} girdi. ` : 'Eşleştirildi. ') +
        (r.uyari ?? '')
      );
      await yukle();
      degisti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Bağlanamadı');
    } finally {
      setCalisan(null);
    }
  }

  const bekleyen = d?.satirlar.filter((s) => !s.bagli) ?? [];

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-label="Alış faturası">
        {!d ? (
          <div className="ccd-inline-note">Yükleniyor…</div>
        ) : (
          <div className="ccd-stack">
            <div className="ccd-row-between">
              <div>
                <div className="ccd-eyebrow ccd-mono">{d.no}</div>
                <h2 className="ccd-heading">{d.supplier?.name ?? 'Tedarikçi belirsiz'}</h2>
                <div className="ccd-meta">
                  {formatTarih(d.date)} · {formatTl(d.totalKurus)} · {d.satirlar.length} satır
                </div>
              </div>
              <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
            </div>

            {d.itirazKalanGun > 0 && (
              <div className="ccd-banner ccd-banner-warning">
                <span className="ccd-banner-icon" aria-hidden="true">⏳</span>
                <div className="ccd-meta">
                  Fiyat itiraz penceresi: <b>{d.itirazKalanGun} gün</b> kaldı.
                  Artış varsa bu süre içinde tedarikçiye bildirin.
                </div>
              </div>
            )}

            {bilgi && (
              <div className="ccd-banner">
                <span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span>
              </div>
            )}
            {hata && (
              <div className="ccd-banner ccd-banner-danger">
                <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
              </div>
            )}

            <div className="ccd-fieldset-title">
              Bağlanmayı bekleyen ({bekleyen.length})
            </div>

            {bekleyen.length === 0 ? (
              <div className="ccd-empty">Tüm satırlar bağlandı.</div>
            ) : bekleyen.map((s) => (
              <div key={s.id} className="ccd-card ccd-stack-sm">
                <div>
                  <b>{s.description}</b>
                  <div className="ccd-meta">
                    {s.supplierCode && (
                      s.supplierCode.startsWith('parasut:')
                        ? <span className="ccd-badge">Paraşüt ürünü</span>
                        : <span className="ccd-mono">{s.supplierCode}</span>
                    )}{s.supplierCode && ' · '}
                    {s.qty} {s.unit ?? ''} × {formatTl(s.unitPriceKurus)}
                  </div>
                </div>

                {s.oneri && (
                  <div className="ccd-meta">
                    Öneri: <b className="ccd-mono">{s.oneri.code}</b> {s.oneri.name}
                  </div>
                )}

                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor={`u${s.id}`}>Ürün</label>
                  <AraSec id={`u${s.id}`} deger={secim[s.id] ?? ''} degis={(v) => setSecim((x) => ({ ...x, [s.id]: v }))}
                          secenekler={urunler.map((u) => ({ id: u.id, ad: `${u.code} — ${u.name}` }))}
                          yerTutucu="Ürün ara: kod ya da ad yazın…" />
                </div>

                {can('purchase.match') && (
                  <div className="ccd-row" style={{ flexWrap: 'wrap' }}>
                    <button className="ccd-btn ccd-btn-primary ccd-btn-sm"
                            disabled={calisan === s.id}
                            onClick={() => bagla(s)}>
                      {calisan === s.id ? 'Bağlanıyor…' : 'Bağla ve stoğa gir'}
                    </button>
                    <button className="ccd-btn ccd-btn-sm" disabled={calisan === s.id}
                            onClick={() => bagla(s, true)}>
                      Yalnız eşleştir
                    </button>
                  </div>
                )}
                {can('purchase.match') && can('product.write') && (
                  <KalemdenUrun ad={s.description} birim={s.unit} calisiyor={calisan === s.id}
                                ikinci="Aç, yalnız eşleştir" ac={(g, ikinci) => void urunAc(s, g, ikinci)} />
                )}
                <div className="ccd-field-hint">
                  "Yalnız eşleştir" stoğa girmez — iade/düzeltme faturalarında ya da
                  birim çevrilemediğinde kullanılır.
                </div>
              </div>
            ))}

            {d.satirlar.some((s) => s.bagli) && (
              <>
                <div className="ccd-fieldset-title">Bağlanmış satırlar</div>
                <div className="ccd-table-wrap">
                  <table className="ccd-table">
                    <thead>
                      <tr>
                        <th className="ccd-th">Satır</th>
                        <th className="ccd-th">Ürün</th>
                        <th className="ccd-th ccd-td-num">Değişim</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.satirlar.filter((s) => s.bagli).map((s) => (
                        <tr key={s.id} className="ccd-tr">
                          <td className="ccd-td">
                            {s.description}
                            <div className="ccd-meta">{s.qty} {s.unit ?? ''} × {formatTl(s.unitPriceKurus)}</div>
                          </td>
                          <td className="ccd-td ccd-mono">{s.bagli!.code}</td>
                          <td className="ccd-td ccd-td-num">
                            {s.priceDeltaPct == null ? <span className="ccd-muted">—</span>
                              : <span className={s.priceDeltaPct > 0 ? 'ccd-kpi-down' : 'ccd-kpi-up'}>
                                  %{s.priceDeltaPct}
                                </span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
